import React, { ReactNode, useEffect } from "react";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
`;

const ModalContent = styled.div<{ $maxWidth?: string }>`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  max-width: ${(p) => p.$maxWidth ?? "500px"};
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  transition: background 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #f5f5f5;
  }

  .dark-mode & {
    color: #d0d0d0;

    &:hover {
      background: #3a3a4e;
    }
  }
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** CSS max-width for the dialog (default 500px). */
  contentMaxWidth?: string;
  children: ReactNode;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  contentMaxWidth,
  children,
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const originalPaddingRight = document.body.style.paddingRight;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Overlay>
      <ModalContent $maxWidth={contentMaxWidth}>
        {title && (
          <h2
            style={{ marginTop: 0, marginBottom: "1.5rem" }}
          >
            {title}
          </h2>
        )}
        <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
        {children}
      </ModalContent>
    </Overlay>
  );
}
